/** Straddle tab: StockMock-style rolling straddle from the EOD archive
 * (FA-4) — any archived session, all three archived underlyings. Owns
 * the pickers (underlying / session date / expiry), the 1m–15m timeframe
 * resample, zoom, and the legend's show/hide state; the drawing lives in
 * StraddleChart. No polling: the series is immutable history that only
 * grows once a day at the 15:40 IST capture. */

import { useMemo, useState, type Dispatch, type ReactElement, type SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { marketApi } from "@/lib/marketApi";
import { ZoomButtons } from "@/components/ui/ZoomButtons";
import type { StraddlePoint, StraddleResponse, StraddleUnderlying } from "@/types/market";
import { deltaTone } from "./driftShared";
import { StraddleBrush } from "./StraddleBrush";
import { fmtPremium, STRADDLE_COLORS, StraddleChart } from "./StraddleChart";
import {
  FULL_RANGE,
  resample,
  sliceRange,
  TIMEFRAMES,
  toggled,
  zoomRange,
  type BrushRange,
  type StraddleSeriesKey,
  type Timeframe,
} from "./straddleMath";

const UNDERLYINGS: readonly StraddleUnderlying[] = ["NIFTY", "SENSEX", "BANKNIFTY"];

// Never mutated — every toggle builds a fresh set via `toggled`.
const DEFAULT_VISIBLE: ReadonlySet<StraddleSeriesKey> = new Set(["spot", "vwap"]);

const LEGEND: readonly { key: StraddleSeriesKey; label: string }[] = [
  { key: "spot", label: "Spot" },
  { key: "vwap", label: "VWAP" },
  { key: "ce", label: "ATM CE" },
  { key: "pe", label: "ATM PE" },
];

export function StraddleTab(): ReactElement {
  const [underlying, setUnderlying] = useState<StraddleUnderlying>("NIFTY");
  const [date, setDate] = useState<string | null>(null);
  const [expiry, setExpiry] = useState<string | null>(null);
  const [tf, setTf] = useState<Timeframe>(1);
  const [range, setRange] = useState<BrushRange>(FULL_RANGE);
  const [visible, setVisible] = useState<ReadonlySet<StraddleSeriesKey>>(DEFAULT_VISIBLE);
  const query = useQuery({
    queryKey: ["market", "straddle", underlying, date, expiry],
    queryFn: () => marketApi.straddle(underlying, date, expiry),
  });
  // The brush shows the FULL resampled session; the chart draws its slice.
  const session = useMemo(() => resample(query.data?.points ?? [], tf), [query.data, tf]);
  const points = useMemo(() => sliceRange(session, range), [session, range]);
  const pickDate = (d: string | null): void => {
    setDate(d);
    setExpiry(null);
    setRange(FULL_RANGE);
  };
  const pickUnderlying = (u: StraddleUnderlying): void => {
    setUnderlying(u);
    pickDate(null);
  };
  return (
    <section className="card mb-4">
      <Header underlying={underlying} points={points} onUnderlying={pickUnderlying} />
      <Controls
        data={query.data}
        tf={tf}
        range={range}
        onDate={pickDate}
        onExpiry={setExpiry}
        onTf={setTf}
        onRange={setRange}
      />
      <Body
        query={query}
        points={points}
        session={session}
        range={range}
        onRange={setRange}
        visible={visible}
        onToggle={(key) => {
          setVisible((prev) => toggled(prev, key));
        }}
      />
    </section>
  );
}

function Header({
  underlying,
  points,
  onUnderlying,
}: Readonly<{
  underlying: StraddleUnderlying;
  points: readonly StraddlePoint[];
  onUnderlying: (u: StraddleUnderlying) => void;
}>): ReactElement {
  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <h3
          className="text-sm text-ink-secondary"
          title="ATM CE + PE premium, re-anchored to the strike nearest spot every minute. Rising = the market paying up for movement (or spot running from the strikes); falling = premium decay winning. From the 15:40 IST archive — 1-min bars, any captured session."
        >
          Rolling Straddle
        </h3>
        {points.length > 1 && <DeltaPill points={points} />}
      </div>
      <div className="flex rounded-ctl border border-line bg-surface-1 p-0.5">
        {UNDERLYINGS.map((u) => (
          <button
            key={u}
            type="button"
            className={cn(
              "rounded-ctl px-2.5 py-1 text-xs font-medium transition-colors",
              u === underlying ? "bg-accent-bg text-accent" : "text-ink-secondary hover:text-ink",
            )}
            onClick={() => {
              onUnderlying(u);
            }}
          >
            {u}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Straddle premium move across the visible window. For a straddle the
 * SIGN of good news is the reader's position, so the pill only reports. */
function DeltaPill({
  points,
}: Readonly<{ points: readonly StraddlePoint[] }>): ReactElement | null {
  const first = points[0];
  const last = points.at(-1);
  if (first === undefined || last === undefined) return null;
  const delta = last.straddle - first.straddle;
  return (
    <span
      className={cn("pill bg-surface-2 font-mono text-xs", deltaTone(delta))}
      title="Straddle premium change across the visible window"
    >
      Δ {delta > 0 ? "+" : ""}
      {fmtPremium(delta)}
    </span>
  );
}

function Controls({
  data,
  tf,
  range,
  onDate,
  onExpiry,
  onTf,
  onRange,
}: Readonly<{
  data: StraddleResponse | undefined;
  tf: Timeframe;
  range: BrushRange;
  onDate: (date: string) => void;
  onExpiry: (expiry: string) => void;
  onTf: (tf: Timeframe) => void;
  onRange: Dispatch<SetStateAction<BrushRange>>;
}>): ReactElement {
  // The buttons and the brush drive ONE window: the divisor shown here is
  // derived from the brush width, so both controls always agree. CEIL, so
  // zoom-out only disables once the window truly spans the session.
  const zoom = Math.min(Math.max(Math.ceil(1 / (range.end - range.start) - 1e-9), 1), 8);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <SessionSelect
        label="Session"
        options={[...(data?.dates ?? [])].reverse()}
        value={data?.trade_date ?? null}
        format={fmtSessionDate}
        onPick={onDate}
      />
      <SessionSelect
        label="Expiry"
        options={data?.expiries ?? []}
        value={data?.expiry ?? null}
        format={fmtSessionDate}
        onPick={onExpiry}
      />
      <div className="ml-auto flex items-center gap-2">
        <TimeframeButtons tf={tf} onTf={onTf} />
        <ZoomButtons
          zoom={zoom}
          onChange={(next) => {
            onRange((prev) => zoomRange(prev, next > zoom ? "in" : "out"));
          }}
        />
      </div>
    </div>
  );
}

function TimeframeButtons({
  tf,
  onTf,
}: Readonly<{ tf: Timeframe; onTf: (tf: Timeframe) => void }>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line bg-surface-1 p-0.5">
      {TIMEFRAMES.map((minutes) => (
        <button
          key={minutes}
          type="button"
          className={cn(
            "rounded-ctl px-2 py-1 text-xs font-medium transition-colors",
            minutes === tf ? "bg-accent-bg text-accent" : "text-ink-secondary hover:text-ink",
          )}
          onClick={() => {
            onTf(minutes);
          }}
        >
          {minutes}m
        </button>
      ))}
    </div>
  );
}

function SessionSelect({
  label,
  options,
  value,
  format,
  onPick,
}: Readonly<{
  label: string;
  options: readonly string[];
  value: string | null;
  format: (iso: string) => string;
  onPick: (value: string) => void;
}>): ReactElement {
  return (
    <label className="flex items-center gap-1.5 text-xs text-ink-secondary">
      {label}
      <select
        aria-label={label}
        className="rounded-ctl border border-line bg-surface-1 px-2 py-1 font-mono text-xs text-ink"
        value={value ?? ""}
        disabled={options.length === 0}
        onChange={(e) => {
          onPick(e.target.value);
        }}
      >
        {options.map((iso) => (
          <option key={iso} value={iso}>
            {format(iso)}
          </option>
        ))}
      </select>
    </label>
  );
}

function Body({
  query,
  points,
  session,
  range,
  onRange,
  visible,
  onToggle,
}: Readonly<{
  query: { isPending: boolean; isError: boolean; data: StraddleResponse | undefined };
  points: readonly StraddlePoint[];
  session: readonly StraddlePoint[];
  range: BrushRange;
  onRange: Dispatch<SetStateAction<BrushRange>>;
  visible: ReadonlySet<StraddleSeriesKey>;
  onToggle: (key: StraddleSeriesKey) => void;
}>): ReactElement {
  if (query.isPending) {
    return <div className="text-sm text-ink-secondary">Loading archived session…</div>;
  }
  if (query.isError) {
    return <div className="text-sm text-danger">Could not load the straddle series.</div>;
  }
  if ((query.data?.dates.length ?? 0) === 0) {
    return (
      <div className="text-sm text-ink-secondary">
        No archived sessions for {query.data?.underlying ?? "this index"} yet — the 15:40 IST
        capture adds one after each trading day.
      </div>
    );
  }
  if (points.length === 0) {
    return (
      <div className="text-sm text-ink-secondary">
        No straddle bars for this session/expiry combination.
      </div>
    );
  }
  return (
    <>
      <StraddleChart points={points} visible={visible} />
      <StraddleBrush points={session} range={range} onRange={onRange} />
      <Legend points={points} visible={visible} onToggle={onToggle} />
    </>
  );
}

function Legend({
  points,
  visible,
  onToggle,
}: Readonly<{
  points: readonly StraddlePoint[];
  visible: ReadonlySet<StraddleSeriesKey>;
  onToggle: (key: StraddleSeriesKey) => void;
}>): ReactElement {
  const last = points.at(-1);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
      <span className="flex items-center gap-1 text-ink-secondary">
        <Swatch color="straddle" />
        Straddle
        {last !== undefined && (
          <span className="font-mono text-ink">{fmtPremium(last.straddle)}</span>
        )}
      </span>
      {LEGEND.map(({ key, label }) => (
        <LegendChip
          key={key}
          seriesKey={key}
          label={label}
          value={last === undefined ? null : legendValue(last, key)}
          isHidden={!visible.has(key)}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

function legendValue(point: StraddlePoint, key: StraddleSeriesKey): number | null {
  if (key === "spot") return point.spot;
  if (key === "vwap") return point.vwap;
  return key === "ce" ? point.ce : point.pe;
}

function LegendChip({
  seriesKey,
  label,
  value,
  isHidden,
  onToggle,
}: Readonly<{
  seriesKey: StraddleSeriesKey;
  label: string;
  value: number | null;
  isHidden: boolean;
  onToggle: (key: StraddleSeriesKey) => void;
}>): ReactElement {
  return (
    <button
      type="button"
      aria-label={`Toggle ${label}`}
      title="Click to hide/show this line"
      className={cn("flex items-center gap-1 text-ink-secondary", isHidden && "opacity-40")}
      onClick={() => {
        onToggle(seriesKey);
      }}
    >
      <Swatch color={seriesKey} />
      <span className={cn(isHidden && "line-through")}>{label}</span>
      {value !== null && <span className="font-mono text-ink">{fmtPremium(value)}</span>}
    </button>
  );
}

function Swatch({ color }: Readonly<{ color: keyof typeof STRADDLE_COLORS }>): ReactElement {
  return (
    <span
      className="inline-block h-0.5 w-3 rounded-full"
      style={{ backgroundColor: STRADDLE_COLORS[color] }}
    />
  );
}

/** "Fri, 07 Aug 2026" from an ISO date — parsed as UTC midnight so the
 * label can never shift a day in any browser timezone. */
function fmtSessionDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
